import _defaultTo from 'lodash/defaultTo';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _toUpper from 'lodash/toUpper';
import _some from 'lodash/some';
// Serialization
import { deserialize, serialize } from 'serializr';
import { parkingSchema, parkingStateSchema } from './schemas';
// Color
import { parkingStatusPalette, occupancyStatePalette, carparkSpacesTypePalette } from '../../components/core/colors';
// Permissions
import permissions from '../../constants/Permissions';
// Fragments
import { parkingFragment, operationFragment, offerFragment } from './fragments';
// Models
import mParkingCategory from './parkingCategory';
// Custom
import { SEARCHFIELD_TYPES, FILTERS_OPERATORS } from '../../constants/App';
import { findInList } from '../../utils/Tools';
import { dateFormatter } from '../../utils/DateUtils';

// Permissions used in model
const {
  pCpm: { parkingCategoryRead, planningRead, customerRead },
} = permissions;

/**
 * Serialization
 */
const deserializeParking = (data) => deserialize(parkingSchema, data);

const serializeParkingAdd = (
  {
    automaticKeypadCodesQuantity,
    begin,
    end,
    comments,
    customerId,
    keypadCodes,
    vehicles,
    insideQuantity,
    outsideQuantity,
    outsideQuantityPermanentExit,
    doors,
    planningId,
    restrictedByForbiddenEntry,
    restrictedByPermanentExit,
    restrictedByForbiddenExit,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    spacesQuantity,
    type,
  },
  hasPermission,
) => ({
  ...serialize(parkingSchema, {
    ...(!_isNil(automaticKeypadCodesQuantity) && { automaticKeypadCodesQuantity }),
    begin,
    ...(!_isNil(comments) && { comments }),
    customerId,
    doors,
    end,
    ...(!_isNil(keypadCodes) && { keypadCodes }),
    ...(hasPermission({ needed: planningRead, permissionsOrContext: 'carparkPermissions' }) && { planningId }),
    ...(!_isNil(restrictedByForbiddenEntry) && { restrictedByForbiddenEntry }),
    ...(!_isNil(restrictedByPermanentExit) && { restrictedByPermanentExit }),
    ...(!_isNil(restrictedByForbiddenExit) && { restrictedByForbiddenExit }),
    ...(!_isNil(restrictedByAntipassbackEntrance) && { restrictedByAntipassbackEntrance }),
    ...(!_isNil(restrictedByAntipassbackExit) && { restrictedByAntipassbackExit }),
    spacesQuantity,
    state: {
      insideQuantity: _defaultTo(insideQuantity, null),
      ...(!_isNil(outsideQuantity) && { outsideQuantity }),
      ...(!_isNil(outsideQuantityPermanentExit) && { outsideQuantityPermanentExit }),
    },
    type,
    vehicles,
  }),
});

const serializeParkingUpdate = (
  {
    begin,
    comments,
    end,
    keypadCodes,
    vehicles,
    doors,
    planningId,
    restrictedByForbiddenEntry,
    restrictedByPermanentExit,
    restrictedByForbiddenExit,
    restrictedByAntipassbackEntrance,
    restrictedByAntipassbackExit,
    spacesQuantity,
  },
  hasPermission,
) => ({
  ...serialize(parkingSchema, {
    begin,
    ...(!_isNil(comments) && { comments }),
    end,
    ...(!_isNil(keypadCodes) && { keypadCodes }),
    doors,
    ...(hasPermission({ needed: planningRead, permissionsOrContext: 'carparkPermissions' }) && {
      planningId: _defaultTo(planningId, null),
    }),
    ...(!_isNil(restrictedByForbiddenEntry) && { restrictedByForbiddenEntry }),
    ...(!_isNil(restrictedByPermanentExit) && { restrictedByPermanentExit }),
    ...(!_isNil(restrictedByForbiddenExit) && { restrictedByForbiddenExit }),
    ...(!_isNil(restrictedByAntipassbackEntrance) && { restrictedByAntipassbackEntrance }),
    ...(!_isNil(restrictedByAntipassbackExit) && { restrictedByAntipassbackExit }),
    spacesQuantity,
    vehicles,
  }),
});

const serializeParkingState = ({ insideQuantity, outsideQuantity, outsideQuantityPermanentExit }) => ({
  state: {
    ...serialize(parkingStateSchema, {
      insideQuantity,
      ...(!_isNil(outsideQuantity) && { outsideQuantity }),
      ...(!_isNil(outsideQuantityPermanentExit) && { outsideQuantityPermanentExit }),
    }),
  },
});

/**
 * Lists
 */

const STATUS_LIST = {
  CREATED: 'created',
  IN_PROGRESS: 'in_progress',
  STARTED: 'started',
  FINISHED: 'finished',
  CANCELLED: 'cancelled',
  IN_OVERSTAY: 'in_overstay',
};

const parkingStatusList = [
  {
    key: STATUS_LIST.CREATED,
    value: STATUS_LIST.CREATED,
    labelId: 'parking.status.created',
    icon: 'parkingStatusCreated',
    color: parkingStatusPalette.created,
  },
  {
    key: STATUS_LIST.IN_PROGRESS,
    value: STATUS_LIST.IN_PROGRESS,
    labelId: 'parking.status.inProgress',
    icon: 'parkingStatusInProgress',
    color: parkingStatusPalette.inProgress,
  },
  {
    key: STATUS_LIST.STARTED,
    value: STATUS_LIST.STARTED,
    labelId: 'parking.status.started',
    icon: 'parkingStatusStarted',
    color: parkingStatusPalette.started,
  },
  {
    key: STATUS_LIST.FINISHED,
    value: STATUS_LIST.FINISHED,
    labelId: 'parking.status.finished',
    icon: 'parkingStatusFinished',
    color: parkingStatusPalette.finished,
  },
  {
    key: STATUS_LIST.CANCELLED,
    value: STATUS_LIST.CANCELLED,
    labelId: 'parking.status.cancelled',
    icon: 'parkingStatusCancelled',
    color: parkingStatusPalette.cancelled,
  },
  {
    key: STATUS_LIST.IN_OVERSTAY,
    value: STATUS_LIST.IN_OVERSTAY,
    labelId: 'parking.status.inOverstay',
    icon: 'parkingStatusInOverstay',
    color: parkingStatusPalette.inOverstay,
  },
];
const parkingTypesList = [
  {
    key: 'assigned',
    value: 'assigned',
    labelId: 'parking.type.assigned',
    icon: 'itemParking',
    color: carparkSpacesTypePalette.assigned,
  },
  {
    key: 'booking',
    value: 'booking',
    labelId: 'parking.type.booking',
    icon: 'itemBooking',
    color: carparkSpacesTypePalette.bookings,
  },
  {
    key: 'regular',
    value: 'regular',
    labelId: 'parking.type.regular',
    icon: 'itemAccessRight',
    color: carparkSpacesTypePalette.withoutBooking,
  },
];

/**
 * Getters
 */
// Status
const parkingGetStatusCreated = parkingStatusList[0];
const parkingGetStatusInProgress = parkingStatusList[1];
const parkingGetStatusStarted = parkingStatusList[2];
const parkingGetStatusFinished = parkingStatusList[3];
const parkingGetStatusCancelled = parkingStatusList[4];
const parkingGetStatusInOverstay = parkingStatusList[5];

// Type
const parkingGetTypeAssigned = parkingTypesList[0];
const parkingGetTypeBooking = parkingTypesList[1];
const parkingGetTypeRegular = parkingTypesList[2];

/**
 * Utils
 */
const parkingTitle = (parking) => {
  if (_has(parking, 'reference')) {
    return `#${parking.reference}`;
  }
  return '';
};

// Status
const parkingStatus = (parking) => findInList(parkingStatusList, 'value', parking, 'status');
const parkingIsCancelled = (parking) => parkingStatus(parking) === parkingGetStatusCancelled;
const parkingIsCreated = (parking) => parkingStatus(parking) === parkingGetStatusCreated;
const parkingIsFinished = (parking) => parkingStatus(parking) === parkingGetStatusFinished;
const parkingIsInOverstay = (parking) => parkingStatus(parking) === parkingGetStatusInOverstay;
const parkingIsInProgress = (parking) => parkingStatus(parking) === parkingGetStatusInProgress;
const parkingIsStarted = (parking) => parkingStatus(parking) === parkingGetStatusStarted;
const parkingGetStartedArray = (parkings) => _filter(parkings, (parking) => parkingIsStarted(parking));
const parkingsAreStartedOrInProgress = (parkings) => _map(_filter(parkings, (parking) => _some([parkingIsStarted(parking), parkingIsInProgress(parking)])));
// Type
const parkingType = (parking) => findInList(parkingTypesList, 'value', parking, 'type');
const parkingIsAssigned = (parking) => parkingType(parking).value === parkingGetTypeAssigned.value;
const parkingIsBooking = (parking) => parkingType(parking).value === parkingGetTypeBooking.value;
const parkingIsRegular = (parking) => parkingType(parking).value === parkingGetTypeRegular.value;
const parkingIsAccessRight = (parking) => _get(parking, 'functionalType', '').toUpperCase() === 'REGULAR';
const parkingIsOnDemand2 = (parking) => _get(parking, 'functionalType', '').toUpperCase() === 'ON_DEMAND';
const parkingIsDefinitiveExit = (parking) => parkingIsFinished(parking);

const parkingHasAccessRestrictions = (parking) => !!_get(parking, 'planning') || _get(parking, 'restrictedByPermanentExit');
const parkingHasAntipassbackRestrictions = (parking) => _get(parking, 'restrictedByAntipassbackEntrance') || _get(parking, 'restrictedByAntipassbackExit');

const parkingStateDesc = (value, spacesQuantity) => {
  if (value > spacesQuantity) {
    return 'over';
  }
  if (value === spacesQuantity) {
    return 'all';
  }
  if (value > 0) {
    return 'some';
  }
  return 'none';
};

const parkingStateInfo = (parking) => {
  const {
    spacesQuantity,
    state: {
      insideQuantity, outsideQuantity, outsideQuantityPermanentExit, unknownQuantity,
    },
  } = parking;

  const insideQuantityDesc = parkingStateDesc(insideQuantity, spacesQuantity);
  const outsideQuantityDesc = parkingStateDesc(outsideQuantity, spacesQuantity);
  const outsideQuantityPermanentExitDesc = parkingStateDesc(outsideQuantityPermanentExit, spacesQuantity);
  const unknownQuantityDesc = parkingStateDesc(unknownQuantity, spacesQuantity);

  return {
    inside: {
      key: 'insideQuantity',
      value: insideQuantity,
      iconId: 'occupancyStateInside',
      labelId: 'vehicles.state.insideQuantity',
      desc: insideQuantityDesc,
      color: occupancyStatePalette[insideQuantityDesc],
    },
    outside: {
      key: 'outsideQuantity',
      value: outsideQuantity,
      iconId: 'occupancyStateOutside',
      labelId: 'vehicles.state.outsideQuantity',
      desc: outsideQuantityDesc,
      color: occupancyStatePalette[outsideQuantityDesc],
    },
    outsidePermanentExit: {
      key: 'outsideQuantityPermanentExit',
      value: outsideQuantityPermanentExit,
      iconId: 'occupancyStateOutside',
      labelId: 'vehicles.state.outsideQuantityPermanentExit',
      desc: outsideQuantityPermanentExitDesc,
      color: occupancyStatePalette[outsideQuantityPermanentExitDesc],
    },
    unknown: {
      key: 'unknownQuantity',
      value: unknownQuantity,
      iconId: 'occupancyStateUnknown',
      labelId: 'vehicles.state.unknownQuantity',
      desc: unknownQuantityDesc,
      color: occupancyStatePalette[unknownQuantityDesc],
    },
  };
};

const parkingActivityFilters = (type, date, timezone) => [
  {
    field: type === 'arrivals' ? 'begin' : 'end',
    operator: SEARCHFIELD_TYPES.date.defaultOperator('after'),
    value: dateFormatter.toServerFormat(date, timezone, { hour: 0, minute: 0, second: 0 }),
  },
  {
    field: type === 'arrivals' ? 'begin' : 'end',
    operator: SEARCHFIELD_TYPES.date.defaultOperator('before'),
    value: dateFormatter.toServerFormat(date, timezone, { hour: 23, minute: 59, second: 59 }),
  },
];

// Get parking category: fallback to customer's one if necessary
const parkingGetParkingCategory = (parking, customer) => _get(parking, 'parkingCategory') || _get(customer || parking.customer, 'parkingCategory');
const parkingGetParkingCategoryId = (parking, customer) => _get(parkingGetParkingCategory(parking, customer), 'id');
const parkingIsOnDemand = (parking, customer) => mParkingCategory.isOnDemand(parkingGetParkingCategory(parking, customer));

// Get parking with type booking
const getParkingsBooking = (parkings) => _map(_filter(parkings, (parking) => parkingIsBooking(parking)));
// Get parking with type regular or assigned
const getParkingsRegularOrAssigned = (parkings) => _map(_filter(parkings, (parking) => _some([parkingIsAssigned(parking), parkingIsRegular(parking)])));
const getParkingsAccessRightOrAssigned = (parkings) => _map(_filter(parkings, (parking) => _some([parkingIsAssigned(parking), parkingIsAccessRight(parking)])));
const getParkingsOnDemand = (parkings) => _map(_filter(parkings, (parking) => parkingIsOnDemand2(parking)));
// Constants
const parkingCostantOnlyBooking = {
  field: 'type',
  operator: FILTERS_OPERATORS[0],
  value: _toUpper(parkingGetTypeBooking.value),
};

// Constants
const parkingCostantNoBookingFilter = [
  {
    field: 'type',
    operator: FILTERS_OPERATORS[0],
    value: _toUpper(parkingGetTypeRegular.value),
  },
  {
    field: 'type',
    operator: FILTERS_OPERATORS[0],
    value: _toUpper(parkingGetTypeAssigned.value),
  },
];

/**
 * GraphQL
 */
const queryPermissions = (hasPermission) => ({
  withCustomer: hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }),
  withParkingCategory: hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }),
  withPlanning: hasPermission({ needed: planningRead, permissionsOrContext: 'carparkPermissions' }),
});

// QUERIES
const queListParkings = (hasPermission) => `
  query ListParkings($input: ListParkingsInput!, $pagination: PaginationInput, $search: ListParkingsSearchInput, $sort: [ListParkingsSortInput]) {
    listParkings(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...parkingFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${parkingFragment(queryPermissions(hasPermission))}
`;

const queListParkingsAnyType = `
  query ListParkingsAnyType($input: ListParkingsInput!,  $pagination: PaginationInput, $search: ListParkingsSearchInput, $sort: [ListParkingsSortInput]) {
    listParkingsAnyType(input: $input, pagination: $pagination, search: $search, sort: $sort) {
        entries {
          id,
          customer {
            firstname,
            lastname,
            email,
            phone
          },
          functionalType
          carpark {
            id
            name
            timezone
          }
          begin,
          reference,
          vehicles {
            licensePlate
          }
          status,
          spacesQuantity,
          state {
            insideQuantity,
            outsideQuantity,
            outsideQuantityPermanentExit,
            unknownQuantity
          }
          purchases {
            type,
            paymentStatus,
            totalAmountInclTax
          }
        },
        pageNumber,
        pageSize,
        totalEntries,
        totalPages
      }
    }
`;

const queGetParking = (hasPermission) => `
  query GetParking($input: GetParkingInput!) {
    getParking(input: $input) {
      ...parkingFragment
    }
  }

  ${parkingFragment(queryPermissions(hasPermission))}
`;

const queListParkingsAnyTypeForManualOpening = `
  query ListParkingsAnyType($input: ListParkingsInput!,  $pagination: PaginationInput, $search: ListParkingsSearchInput, $sort: [ListParkingsSortInput]) {
    listParkingsAnyType(input: $input, pagination: $pagination, search: $search, sort: $sort) {
        entries {
          id,
          customer {
            firstname,
            lastname,
            parkingCategory {
              active,
              name
            }
          },
          functionalType
          begin,
          end,
          reference,
          vehicles {
            licensePlate
          }
          inDropOffPeriod,
          billingStatus,
          status,
          spacesQuantity,
          state {
            insideQuantity,
            outsideQuantity,
            outsideQuantityPermanentExit,
            unknownQuantity
          }
          purchases {
            type,
            paymentStatus,
            totalAmountInclTax,
            discountedAmountInclTax,
            offers {
               end,
               type
             }
          }
        },
      }
    }
`;

// MUTATIONS
const mutAddParking = `
  mutation AddParking($input: AddParkingInput!) {
    addParking(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutUpdateParking = `
  mutation UpdateParking($input: UpdateParkingInput!) {
    updateParking(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutUpdateParkingState = `
  mutation UpdateParkingState($input: UpdateParkingStateInput!) {
    updateParkingState(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutDeleteParking = `
  mutation DeleteParking($input: DeleteParkingInput!) {
    deleteParking(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutCheckoutOnDemandParking = (hasPermission) => `
  mutation CheckoutOnDemandParking($input: CheckoutOnDemandParkingInput!) {
    checkoutOnDemandParking(input: $input) {
      ...offerFragment
    }
  }

  ${offerFragment(queryPermissions(hasPermission))}
`;

const mutValidateCheckoutOnDemandParking = `
  mutation ValidateCheckoutOnDemandParking($input: ValidateCheckoutOnDemandParking!) {
    validateCheckoutOnDemandParking(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

/**
 * Model
 */
const parking = {
  // Name and icon
  name: 'parking',
  icon: 'parking',

  // Constants
  NO_BOOKING_FILTER: parkingCostantNoBookingFilter,
  STATUS_LIST,

  // Serialization
  deserialize: deserializeParking,
  serializeAdd: serializeParkingAdd,
  serializeUpdate: serializeParkingUpdate,
  serializeState: serializeParkingState,

  // Lists
  statusList: parkingStatusList,
  typesList: parkingTypesList,

  // Getters
  getStatusCreated: parkingGetStatusCreated,
  getStatusFinished: parkingGetStatusFinished,
  getStatusInOverstay: parkingGetStatusInOverstay,
  getStatusInProgress: parkingGetStatusInProgress,
  getStatusStarted: parkingGetStatusStarted,
  getStatusCancelled: parkingGetStatusCancelled,

  getTypeAssigned: parkingGetTypeAssigned.value,
  getTypeBooking: parkingGetTypeBooking.value,
  getTypeRegular: parkingGetTypeRegular.value,

  // Constants
  ONLY_BOOKING: parkingCostantOnlyBooking,

  // Utils
  title: parkingTitle,
  status: parkingStatus,
  isCancelled: parkingIsCancelled,
  isCreated: parkingIsCreated,
  isAssigned: parkingIsAssigned,
  isBooking: parkingIsBooking,
  isDefinitiveExit: parkingIsDefinitiveExit,
  isFinished: parkingIsFinished,
  isInOverstay: parkingIsInOverstay,
  isInProgress: parkingIsInProgress,
  isOnDemand: parkingIsOnDemand,
  isStarted: parkingIsStarted,
  getStartedArray: parkingGetStartedArray,
  hasAccessRestrictions: parkingHasAccessRestrictions,
  hasAntipassbackRestrictions: parkingHasAntipassbackRestrictions,
  stateInfo: parkingStateInfo,
  activityFilters: parkingActivityFilters,
  getParkingCategory: parkingGetParkingCategory,
  getParkingCategoryId: parkingGetParkingCategoryId,
  type: parkingType,
  getParkingsBooking,
  getParkingsRegularOrAssigned,
  getParkingsAccessRightOrAssigned,
  getParkingsOnDemand,
  areStartedOrInProgress: parkingsAreStartedOrInProgress,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'status', dir: 'asc' },
        { field: 'begin', dir: 'desc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'reference',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      sm: 1 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      md: 3 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      lg: 4 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      xl: 4 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
    }),
    columns: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'begin',
        labelId: 'field.beginDate',
        field: 'begin',
        sortable: true,
        className: 'hidden-sm-down',
      },
      {
        key: 'end',
        labelId: 'field.endDate',
        field: 'end',
        sortable: true,
        className: 'hidden-sm-down',
      },
      {
        key: 'customer',
        permissions: {
          needed: customerRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.carparkUser',
        field: 'customerFullName',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
      },
      {
        key: 'occupancy',
        labelId: 'field.occupancy',
        field: 'occupancy',
        sortable: false,
        className: 'table-content-2 hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'parkingCategoryId',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'parkingCategoryId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'parkingCategoryId',
        },
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
      {
        key: 'carparkCustomer',
        permissions: {
          needed: customerRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.carparkUser',
        field: 'carparkCustomer',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'customerId',
          key: 'id',
        },
      },
      {
        key: 'keypadCode',
        labelId: 'field.keypadCode',
        field: 'keypadCode',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'keypadCode',
        },
      },
      {
        key: 'licensePlate',
        labelId: 'field.licensePlate',
        field: 'licensePlate',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'licensePlate',
        },
      },
      {
        key: 'beginAfter',
        labelId: 'field.afterDateBegin',
        field: 'beginAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'beginBefore',
        labelId: 'field.beforeDateBegin',
        field: 'beginBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'endAfter',
        labelId: 'field.afterDateEnd',
        field: 'endAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'endBefore',
        labelId: 'field.beforeDateEnd',
        field: 'endBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'end',
        },
      },
    ],
  },

  // Accesses list features
  accessesListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [{ field: 'date', dir: 'desc' }],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 2,
      lg: 4,
      xl: 4,
    }),
    columns: [
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: false,
      },
      {
        key: 'door',
        labelId: 'field.access',
        field: 'door',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'source',
        labelId: 'field.accessWay',
        field: 'source',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'result',
        labelId: 'field.result',
        field: 'result',
        sortable: false,
        className: 'hidden-xs-down',
      },
      {
        key: 'resultSmDown',
        labelId: 'field.result',
        field: 'result',
        sortable: false,
        className: 'hidden-sm-up table-content-1',
      },
    ],

    searchFields: [],
  },

  // GraphQL
  queListParkings,
  queListParkingsAnyType,
  queGetParking,
  queListParkingsAnyTypeForManualOpening,
  mutAddParking,
  mutUpdateParking,
  mutUpdateParkingState,
  mutDeleteParking,
  mutCheckoutOnDemandParking,
  mutValidateCheckoutOnDemandParking,
};

module.exports = parking;



// WEBPACK FOOTER //
// ./models/cpm/parking.js